import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID!,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID!,
};

const userPool = new CognitoUserPool(poolData);

export interface AuthUser {
  sub: string;
  email: string;
  role: string;
  token: string;
}

export type LoginResult =
  | { type: 'success'; user: AuthUser }
  | { type: 'newPasswordRequired'; complete: (newPassword: string) => Promise<AuthUser> };

function sessionToUser(email: string, session: CognitoUserSession): AuthUser {
  const token = session.getIdToken().getJwtToken();
  const payload = session.getIdToken().decodePayload();
  return {
    sub: payload.sub,
    email: payload.email || email,
    role: payload['custom:role'] || 'viewer',
    token,
  };
}

export function login(email: string, password: string): Promise<LoginResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ type: 'success', user: sessionToUser(email, session) }),
      onFailure: reject,
      newPasswordRequired: () => {
        resolve({
          type: 'newPasswordRequired',
          complete: (newPassword) =>
            new Promise<AuthUser>((res, rej) => {
              cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
                onSuccess: (session) => res(sessionToUser(email, session)),
                onFailure: rej,
              });
            }),
        });
      },
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: reject,
      inputVerificationCode: () => resolve(), // code delivery confirmed — UI moves to next step
    });
  });
}

export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: reject,
    });
  });
}

export function logout(): void {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}

export function getCurrentSession(): Promise<AuthUser | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(sessionToUser('', session));
    });
  });
}
